import { NailAppointmentItemFragment } from '@client/graphql/types/graphql.ts';
import { Fragment } from 'react';
import { useTranslate } from '@tolgee/react';
import { groupBy } from 'graphql/jsutils/groupBy';
import moment from 'moment-timezone';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context.ts';
import Table from '@packages/ui/table/table.tsx';
import { TableSection } from '@packages/ui/table/table-section.tsx';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import TableRow from '@packages/ui/table/table-row.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import { ChatBubbleOvalLeftEllipsisIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';
import { AppointmentTableListConfirmationStatus } from '@client/page/management/appointment/list/component/appointment-table-list-confirmation-status.tsx';
import VirtualUserSelectionDepend from '@client/page/management/appointment/list/component/virtual-user-selection-depend.tsx';
import { getFragmentData } from '@client/graphql/types';
import { PickedNailAppointmentEmployeeFragment } from '@packages/network-graphql/common/appointment-picked-team.ts';
import EmployeeDot from '@client/page/management/appointment/employee/component/employee-dot.tsx';

export default function AppointmentHistoryTable(props: {
  loading?: boolean;
  items: NailAppointmentItemFragment[];
  onClick?: (id: string) => void;
  error?: string;
  refetch?: () => void;
}) {
  const { items, loading } = props;
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  if (items.length === 0 && loading == false) {
    return <div className="px-6 py-4">{t('appointment.no-appointment')}</div>;
  }

  const dateGroups = groupBy(items, (item) => {
    return moment(item.startTime).tz(company.settings.timezone).format('DD MMM');
  });

  const dates = Array.from(dateGroups, ([date, appointments]) => {
    const sortedReservations = Array.from(appointments);
    sortedReservations.sort((a, b) => {
      return moment(a.startTime).isBefore(moment(b.startTime)) ? 1 : -1;
    });

    return {
      date,
      appointments: sortedReservations,
    };
  });

  // Sort by date
  dates.sort((a, b) => {
    return moment(a.date).isBefore(moment(b.date)) ? 1 : -1;
  });

  return (
    <>
      <Table
        loading={loading}
        data={dates}
        build={(item) => (
          <Fragment key={item.date}>
            <TableSection key={item.date} colSpan={6}>
              <div className="flex justify-start">
                <span className="font-bold text-blue-500">
                  {item.date == moment().tz(company.settings.timezone).format('DD MMM')
                    ? t('appointment.history.list.section.today', 'Today')
                    : item.date}
                </span>
              </div>
            </TableSection>

            {item.appointments.map((appointment) => (
              <AppointmentRow
                key={appointment.id}
                item={appointment}
                timezone={company.settings.timezone}
                onClick={() => props.onClick?.(appointment.id)}
              />
            ))}
          </Fragment>
        )}
        rounded={false}
        error={props.error}
        refetch={props.refetch}
      >
        {/*Desktop*/}
        <TableHeaderColumn className="hidden sm:table-cell w-28 xl:w-36 2xl:w-48">
          {t('common.table-header-column.status', 'Status')} / {t('common.table-header-column.time', 'Time')}
        </TableHeaderColumn>
        <TableHeaderColumn className="hidden xl:table-cell xl:w-44 2xl:w-60">
          {t('common.table-header-column.employee', 'Employee')}
        </TableHeaderColumn>
        <TableHeaderColumn className="hidden sm:table-cell xl:w-44 2xl:w-60">
          {t('common.table-header-column.guest', 'Guest')}
        </TableHeaderColumn>
        <TableHeaderColumn className="hidden sm:table-cell">
          {t('common.table-header-column.service', 'Service')}
        </TableHeaderColumn>
        <TableHeaderColumn className="hidden xl:table-cell">
          {t('common.table-header-column.message', 'Message')}
        </TableHeaderColumn>

        {/*Mobile*/}
        <TableHeaderColumn className="sm:hidden w-14">
          {t('common.table-header-column.status', 'Status')}
        </TableHeaderColumn>

        <TableHeaderColumn className="sm:hidden max-w-16">
          {t('common.table-header-column.time-service', 'Time / Service')}
        </TableHeaderColumn>

        <TableHeaderColumn className="sm:hidden max-w-16">
          {t('common.table-header-column.name-assign', 'Guest / Assigned')}
        </TableHeaderColumn>

        <TableHeaderColumn className="w-4"></TableHeaderColumn>
      </Table>
    </>
  );
}

function AppointmentRow(props: { item: NailAppointmentItemFragment; onClick: () => void; timezone: string }) {
  const { item, timezone, onClick } = props;

  const employee = getFragmentData(PickedNailAppointmentEmployeeFragment, item.employees);
  const userId = employee[0] && employee[0].user.userId ? employee[0].user.userId : '';
  const userFirstName = employee[0] ? employee[0].user.firstName : '';
  const colorCode = employee[0] ? employee[0].user.colorCode : '';
  const serviceName = item.products[0] ? item.products[0].pickedProduct.title : '';

  return (
    <>
      <TableRow key={item.id} className="cursor-pointer overflow-hidden" onClick={onClick}>
        {/*Desktop*/}
        <TableRowColumn className="hidden sm:table-cell sm:w-40 xl:w-36 2xl:w-48">
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="w-full h-full relative flex items-center gap-2"
          >
            <AppointmentTableListConfirmationStatus appointmentId={item.id} status={item.confirmationStatus} />
            <p className="font-bold">{nailAppointmentTimeFormat(item.startTime ?? '', timezone)}</p>
          </div>
        </TableRowColumn>

        <TableRowColumn className="hidden xl:table-cell xl:w-44 2xl:w-60">
          <div
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="w-full h-full relative"
          >
            <VirtualUserSelectionDepend appointmentId={item.id} userFirstName={userFirstName} userId={userId} />
          </div>
        </TableRowColumn>

        {/* Name */}
        <TableRowColumn className="hidden sm:table-cell max-w-[80px] xl:w-44 2xl:w-60">
          <span>
            {item.customerVariant.name.length <= 15
              ? item.customerVariant.name
              : `${item.customerVariant.name.slice(0, 15)}...`}
          </span>
          {/*<span className="md:hidden"> ({item.seats})</span>*/}
        </TableRowColumn>

        {/* Service */}
        <TableRowColumn className="hidden sm:table-cell max-w-12 xl:max-w-32 truncate">
          <span className="font-semibold truncate ">{serviceName}</span>
          {/*<span className="md:hidden"> ({item.seats})</span>*/}
        </TableRowColumn>

        <TableRowColumn className="hidden xl:table-cell max-w-0.5 truncate overflow-hidden break-all">
          {item.message}
        </TableRowColumn>

        <TableRowColumn className="hidden space-x-4">
          <div className="flex items-center justify-end ">
            <ChatBubbleOvalLeftEllipsisIcon
              className={classNames('fill-yellow-500 w-4 h-4 xl:hidden', {
                hidden: item.message?.length === 0,
              })}
            />
            <ChevronRightIcon className="w-7 fill-gray-400" />
          </div>
        </TableRowColumn>

        {/*Mobile*/}
        <TableRowColumn className="sm:hidden w-14">
          <AppointmentTableListConfirmationStatus appointmentId={item.id} status={item.confirmationStatus} />
        </TableRowColumn>

        <TableRowColumn className="sm:hidden ">
          <p className="font-bold">{nailAppointmentTimeFormat(item.startTime ?? '', timezone)}</p>
          <p className="truncate text-gray-400 max-w-20 min-[375px]:max-w-28 min-[425px]:max-w-36 truncate">
            {serviceName}
          </p>
        </TableRowColumn>

        <TableRowColumn className="sm:hidden space-y-1">
          <p className="text-gray-900 max-w-20 min-[375px]:max-w-28 min-[425px]:max-w-36 truncate">
            {item.customerVariant.name}
          </p>
          <p className="flex">
            <EmployeeDot colorCode={colorCode} />
            <span className="text-gray-400 max-w-20 min-[375px]:max-w-24 min-[425px]:max-w-28 truncate">
              {userFirstName}
            </span>
          </p>
        </TableRowColumn>

        <TableRowColumn className="xl:hidden w-4">
          <div className="flex items-center justify-end">
            <ChatBubbleOvalLeftEllipsisIcon
              className={classNames('fill-yellow-500 w-4 h-4 xl:hidden', {
                hidden: item.message?.length === 0,
              })}
            />
          </div>
        </TableRowColumn>
      </TableRow>
    </>
  );
}

function nailAppointmentTimeFormat(dateString: string, timezone: string): string {
  const date = moment(dateString).tz(timezone);
  return date.format('HH:mm');
}
